import { type HTMLAttributes, type ReactNode } from 'react';
import { Flex, Text, TextProps } from '@/components/common';
import { RadixPositions, type RadixColors, type RadixTextSize } from './RadixProps';

type TextSizeIndex = Extract<NonNullable<RadixTextSize>, string>;

interface UnreadDotProps extends Omit<
  HTMLAttributes<HTMLSpanElement>,
  'color' | 'size' | 'children'
> {
  position?: RadixPositions;
  /** Radix theme color for the dot. Defaults to the theme's accent color. */
  color?: RadixColors;
  /** Radix theme text size for the dot. The dot scales with the text size. */
  size?: TextSizeIndex;
  /** Optional text to render inside the dot (e.g. an unread count). */
  children?: ReactNode;
}

const TEXT_SIZE: Record<TextSizeIndex, NonNullable<TextProps['size']>> = {
  1: '0.5',
  2: '1',
  3: '1',
  4: '2',
  5: '2',
  6: '3',
  7: '3',
  8: '4',
  9: '4',
} as const;

function UnreadDot({ color, size = '2', style, position, children, ...props }: UnreadDotProps) {
  const hasChildren = Boolean(children);
  const calculatedSize = Number(size) / 6;
  const height = `${size}em`;
  return (
    <Flex
      as="span"
      position={position}
      data-accent-color={color}
      align="center"
      justify="center"
      style={{
        borderRadius: '100%',
        backgroundColor: 'var(--accent-9)',
        color: 'var(--accent-contrast)',
        ...(hasChildren
          ? { minWidth: height, minHeight: height }
          : { width: `${calculatedSize}em`, height: `${calculatedSize}em` }),
        ...style,
      }}
      {...props}
    >
      {hasChildren && (
        <Text as="span" size={TEXT_SIZE[size]} style={{ color: 'var(--accent-contrast)' }}>
          {children}
        </Text>
      )}
    </Flex>
  );
}

export { UnreadDot };
export type { UnreadDotProps };
